import Select, {components} from 'react-select'
import './index.scss'
import {ArrowDownIcon, ArrowUpIcon, CloseIcon, HelpIcon} from '../../../icons'
import Error from '../error'
import InputInfo from '../inputInfo'
import classNames from 'classnames'
import {useRef, useState, useEffect} from 'react'
import useOnClickOutside from '../../../utils/hooks/useOutsideClick'
import {colors} from '../../../utils/colors'
import {useSelector} from 'react-redux'
import Checkbox from '../checkbox'

const CustomSelect = ({placeholderAsLabel = true, showSelectedOptions = false, isCheckboxDisabled = false, ...props}) => {
    const [helpInfoIsActive, setHelpInfoIsActive] = useState(props.activeInfo)
    const [isAllSelected, setIsAllSelected] = useState(props.isAllSelected)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)

    const ref = useRef()
    const infoRef = useRef()

    useOnClickOutside(infoRef, () => setHelpInfoIsActive(false))

    const DropdownIndicator = props => {
        return (
            <components.DropdownIndicator {...props}>
                {props.selectProps.menuIsOpen ? <ArrowUpIcon width={12} height={10} stroke={colors.colorPrimary}/> :
                    <ArrowDownIcon width={12} height={10} stroke={colors.colorPrimary}/>}
            </components.DropdownIndicator>
        )
    }

    const ClearIndicator = props => {
        return <components.ClearIndicator {...props}>
            <CloseIcon width={14} height={14} stroke={colors.colorPrimary}/>
        </components.ClearIndicator>
    }

    const ValueContainer = ({children, ...localProps}) => {
        let [values, input] = children
        if (Array.isArray(values) && !props.showLabel && !isCheckboxDisabled) {
            const plural = values.length === 1 ? '' : 's'
            values = `${values.length} item${plural} selected`
        }
        return (
            <components.ValueContainer {...localProps}>
                {isCheckboxDisabled ? values :
                    placeholderAsLabel && props.value?.length && props.isMulti ?
                        <span className="multi-value">{values}</span> : values}
                {input}
            </components.ValueContainer>
        )
    }

    const CustomMultiValueLabel = (props) => {
        return (
            <components.MultiValueLabel {...props}>
                <div
                    title={props.data.label}
                    style={{
                        maxWidth: '25px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {props.data.label}
                </div>
            </components.MultiValueLabel>
        )
    }

    const MultiValueContainer = ({data, selectProps, ...props}) => {
        const maxToShow = 3
        const selected = selectProps.value
        const overflow = selected.length - maxToShow
        const index = selected.findIndex((i) => i.value === data.value)

        if (index < maxToShow) {
            return <components.MultiValueContainer data={data} index={index} selectProps={selectProps} {...props} />
        }

        if (index === maxToShow) {
            return (
                <div>
              ... +{overflow}
                </div>
            )
        }

        return null
    }

    const selectComponents = {
        DropdownIndicator,
        ClearIndicator,
        IndicatorSeparator: () => null,
        ValueContainer: ValueContainer,
        ...showSelectedOptions && {
            MultiValueLabel: CustomMultiValueLabel,
            MultiValueContainer
        }
    }

    const formatOptionLabelWithIcon = data => {
        return <div className="d-flex align-items-center gap-half">
            {data?.icon}
            {data?.label}
        </div>
    }

    const formatOptionLabel = (data, {selectValue}) => {
        const isChecked = selectValue?.some(item => item?.value === data?.value)
        return <div className="d-flex align-items-center multi-option gap-half">
            <div className="select-checkbox">
                <Checkbox type="checkbox" checked={isChecked}/>
            </div>
            {data.label}
        </div>
    }

    const isOptionDisabled = option => {
        if (props.limit) {
            return props.value?.length === props.limit &&
                !props.value.map(item => item?.value).includes(option.value)
        }

        if (option?.disabled) {
            return true
        }

        return typeof props.isOptionDisabled === 'function' ? props.isOptionDisabled(option) : false
    }

    const handleChange = (value) => {
        setIsAllSelected(false)
        props.onChange(props.name, value)

        if (props?.showLabel && props?.options?.length === value?.length) {
            ref?.current?.props?.onMenuClose()
        }
    }

    const setHelpInfoStatus = () => {
        setHelpInfoIsActive(!helpInfoIsActive)
        typeof props.handleInfo === 'function' && props.handleInfo(!helpInfoIsActive)
    }

    useEffect(() => {
        setHelpInfoIsActive(props.activeInfo)
    }, [props.activeInfo])

    const filteredOptions = props?.options?.filter(option => option?.isChecked)

    return (
        <>
            <div className={classNames({
                'w-100 form-element': true,
                [props.containerClassName]: props.containerClassName
            })}>
                <div className="select-container" id={`${isCheckboxDisabled || props.isAllSelected ? 'custom-select-additional' : ''}`}>
                    <Select
                        {...props}
                        ref={ref}
                        options={props.options}
                        components={selectComponents}
                        classNamePrefix="custom-select"
                        className={classNames(props?.className, {
                            'dark': isDarkMode,
                            'has-label': props?.hasLabel,
                            'custom-select-container': true,
                            'error': props.error
                        })}
                        value={isAllSelected ? filteredOptions : props.value}
                        onChange={handleChange}
                        isSearchable={!!props?.isSearchable}
                        closeMenuOnSelect={!props.isMulti}
                        blurInputOnSelect={props.blurInputOnSelect ? !props.blurInputOnSelect : !props.isMulti}
                        isDisabled={props.isDisabled}
                        isClearable={props?.isClearable}
                        menuPosition={props.menuPosition || 'relative'}
                        hideSelectedOptions={props.showLabel !== undefined && !showSelectedOptions}
                        isOptionDisabled={isOptionDisabled}
                        formatOptionLabel={props.options?.every(item => item?.icon) ?
                            formatOptionLabelWithIcon :
                            props.isMulti ?
                                formatOptionLabel :
                                null}
                    />
                    {placeholderAsLabel && (props.isMulti ? props.value?.length : props.value) ? (
                        <span className={classNames('custom-placeholder ', {
                            disabled: props.isDisabled,
                            dark: isDarkMode
                        })}>{props.placeholder}</span>
                    ) : <></>}
                    {props.infoText && (
                        <div ref={infoRef} className={`help ${helpInfoIsActive && 'active'}`} onClick={setHelpInfoStatus}>
                            <span><HelpIcon width={props?.infoIconSize} height={props?.infoIconSize}/></span>
                            <InputInfo text={props.infoText}/>
                        </div>
                    )}
                    {props.customIcon && (
                        <div className="help" onClick={props?.customIconOnclick}>
                            <span>{props.customIcon}</span>
                        </div>
                    )}
                </div>
                {props.error && <Error error={props.error} hasIcon={props?.hasIcon}/>}
            </div>
        </>
    )
}

export default CustomSelect
